import type { ReactNode } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Badge, Card, cn } from '@design-system';
import {
  careEnvironmentLabel,
  carePhaseLabel,
  fundingPolicyTypeLabel,
} from '@/features/projection-details/glossary/enumLabels';
import type { WizardCalcSnapshotRunResultSummary } from '../api/wizard-calc-snapshots-api';

export type CalcSnapshotMetricTone = 'neutral' | 'success' | 'warning' | 'danger';

export const CALC_SNAPSHOT_RUN_ERROR_MESSAGE =
  'The test run could not be completed. Please try again.';

interface CalcSnapshotMetricCardProps {
  label: string;
  value: ReactNode;
  tone?: CalcSnapshotMetricTone;
  valueClassName?: string;
}

export function CalcSnapshotMetricCard({
  label,
  value,
  tone = 'neutral',
  valueClassName,
}: CalcSnapshotMetricCardProps) {
  const toneClass = {
    neutral: 'text-gray-950',
    success: 'text-green-700',
    warning: 'text-amber-700',
    danger: 'text-red-700',
  }[tone];

  return (
    <Card className="gap-1 px-4 py-4 shadow-none">
      <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">{label}</p>
      <p className={cn('text-xl font-semibold', toneClass, valueClassName)}>{value}</p>
    </Card>
  );
}

export function ResultBadge({ result }: { result: WizardCalcSnapshotRunResultSummary }) {
  return (
    <Badge variant={result.passed ? (result.warningCount ? 'warning' : 'success') : 'destructive'}>
      {result.passed ? <CheckCircle2 /> : <XCircle />}
      {result.passed ? (result.warningCount ? 'Warning' : 'Passed') : 'Failed'}
    </Badge>
  );
}

interface FormatSnapshotCurrencyOptions {
  maximumFractionDigits?: number;
}

export function formatSnapshotCurrency(
  value: number | null | undefined,
  { maximumFractionDigits = 0 }: FormatSnapshotCurrencyOptions = {},
): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits,
  }).format(value);
}

export function formatSnapshotDateTime(value: string): string {
  return new Date(value).toLocaleString();
}

export function getCalcSnapshotErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export interface ParsedCalcSnapshotGitRevision {
  sha: string | null;
  dirty: boolean;
}

export function parseCalcSnapshotGitRevision(value: string | null): ParsedCalcSnapshotGitRevision {
  if (!value) return { sha: null, dirty: false };
  const dirty = value.endsWith('-dirty');
  const sha = dirty ? value.slice(0, -'-dirty'.length) : value;
  return { sha: /^[0-9a-f]{7,40}$/i.test(sha) ? sha : null, dirty };
}

export function formatCalcSnapshotGitRevision(
  value: string | null,
  missingLabel = 'Local working tree',
): string {
  const revision = parseCalcSnapshotGitRevision(value);
  if (!revision.sha) return missingLabel;
  return `${revision.sha.slice(0, 10)}${revision.dirty ? ' + local changes' : ''}`;
}

export function buildCalcSnapshotCommitRange(
  previous: ParsedCalcSnapshotGitRevision,
  current: ParsedCalcSnapshotGitRevision,
): string | null {
  if (
    !previous.sha ||
    !current.sha ||
    previous.sha === current.sha ||
    previous.dirty ||
    current.dirty
  ) {
    return null;
  }
  return `${previous.sha}..${current.sha}`;
}

const SOURCE_FIELD_LABELS: Record<string, string> = {
  careStartAge: 'Care Start Age',
  annualHealthCareInflationRate: 'Annual Healthcare Inflation Rate',
  earlyCare: 'Early Care',
  moderateCare: 'Moderate Care',
  fullCare: 'Full Care',
  clientAddedDateTime: 'Client Added Date',
  clientBirthYear: 'Birth Year',
  clientBirthDate: 'Date of Birth',
  clientHasStartedLtc: 'Already Receiving Long-Term Care',
  ltcDurationYears: 'Predicted Care Duration',
  discountRate: 'Discount Rate',
  policyType: 'Policy Type',
  policyMaximumBenefitAmount: 'Max Benefit',
  policyContinuationBenefitAmount: 'COB Amount',
  policyMinimumGuaranteedBenefitAmount: 'Min Guaranteed DB',
  policyBaseBenefitAmountAtIssue: 'Base Benefit at Issue',
  policyContinuationBenefitAmountAtIssue: 'Continuation Benefit at Issue',
  policyPremiumStartYear: 'Policy Purchase Year',
  policyPremiumMonthlyCost: 'Monthly Premium',
  policyPremiumIncreaseRate: 'Premium Increase Rate',
  policyPremiumIncreaseYears: 'Premium Increase Years',
  policyLimitedPayYears: 'Limited-Pay Years',
  policyLumpSumPayment: 'Lump-Sum Payment',
  dailyHomeBenefitAmount: 'Home Care Max',
  dailyIndependentLivingBenefitAmount: 'Independent Living Max',
  dailyAssistedLivingBenefitAmount: 'Assisted Living Max',
  dailyNursingHomeBenefitAmount: 'Nursing Home Max',
  dailyHomeInflationProtectionPercent: 'Home Care Inflation Protection',
  dailyIndependentLivingInflationProtectionPercent: 'Independent Living Inflation Protection',
  dailyAssistedLivingInflationProtectionPercent: 'Assisted Living Inflation Protection',
  dailyNursingHomeInflationProtectionPercent: 'Nursing Home Inflation Protection',
  homeCareWaitingPeriodDays: 'Home Care Waiting Period',
  facilityCareWaitingPeriodDays: 'Facility Care Waiting Period',
  hasRetroactiveWaitingPeriodReimbursement: 'Waiting Period Reimbursement',
  policyBenefitPeriodMonths: 'Benefit Period',
  homeCareBenefitPeriodMonths: 'Home Care Benefit Period',
  policyAccelerationBenefitDurationMonths: 'Acceleration Duration',
  policyEobBenefitDurationMonths: 'EOB Duration',
  policyInflationProtection: 'Inflation Protection',
  policyInflationProtectionOnFaceAmount: 'Inflation on Face',
  policyInflationProtectionOnCOBAmount: 'Inflation on COB',
  policyInflationProtectionYears: 'Inflation Protection Years',
  isSimpleInflationProtection: 'Inflation Protection Type',
  isIndemnityPolicyPayment: 'Payment Type',
  policyIndemnityBenefitPercent: 'Indemnity Benefit Percent',
  policyBenefitIsUnlimited: 'Unlimited Benefit',
  policyBenefitUtilizedToDate: 'Benefit Utilized to Date',
  isFast50Election: 'Fast-50 Election',
  isPrimaryPolicyHolder: 'Primary Policyholder',
  isDeactivated: 'Policy Deactivated',
  hasPolicyFunding: 'Policy Included',
};

const SOURCE_CURRENCY_FIELD =
  /(Amount|Cost|Value|Balance|Assets|Income|Expense|Payment|Premium|UtilizedToDate)$/;
const SOURCE_PERCENT_FIELD = /(Rate|Percent|Ratio|InflationProtection)$/;
const SOURCE_DAILY_BENEFIT_FIELD = /^daily.*BenefitAmount$/;

export function formatCalcSnapshotSourceFieldLabel(path: string): string {
  const baseLabel = contextualSourceFieldLabel(path);
  return path.includes('.partner.') ? `Partner · ${baseLabel}` : baseLabel;
}

export function formatCalcSnapshotSourceValue(path: string, value: unknown): string {
  if (value === undefined || value === null) return 'Not set';

  const key = sourceLeafKey(path);
  if (key === 'policyType') return fundingPolicyTypeLabel(value as number | string);
  if (path.includes('.careEnvironmentSelections.') && typeof value === 'number') {
    return careEnvironmentLabel(value);
  }
  if (
    (path.includes('.careEnvironmentCosts.') ||
      path.includes('.clientLocalCareEnvironmentCosts.')) &&
    typeof value === 'number'
  ) {
    if (key === 'percent') return formatSourcePercent(value);
    if (key === 'rateAmount' || key === 'originalRateAmount' || /^\d+$/.test(key)) {
      return formatSnapshotCurrency(value, { maximumFractionDigits: 2 });
    }
  }
  if (/careEnvironment/i.test(key) && typeof value === 'number') {
    return careEnvironmentLabel(value);
  }
  if (/carePhase/i.test(key) && typeof value === 'number') return carePhaseLabel(value);

  if (typeof value === 'boolean') {
    if (key === 'isIndemnityPolicyPayment') return value ? 'Indemnity' : 'Reimbursement';
    if (key === 'isSimpleInflationProtection') return value ? 'Simple' : 'Compound';
    if (key === 'hasRetroactiveWaitingPeriodReimbursement') {
      return value ? 'Reimbursed' : 'Not reimbursed';
    }
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'number') {
    if (key === 'careStartAge' || /Age$/.test(key)) return `${formatSourceNumber(value)} years old`;
    if (path.includes('.carePhaseDurationMonths.')) {
      return `${formatSourceNumber(value)} months`;
    }
    if (/Days$/.test(key)) return `${formatSourceNumber(value)} days`;
    if (/Months$/.test(key)) return `${formatSourceNumber(value)} months`;
    if (/Years$/.test(key) && !/Year$/.test(key)) return `${formatSourceNumber(value)} years`;
    if (SOURCE_PERCENT_FIELD.test(key)) return formatSourcePercent(value);
    if (SOURCE_DAILY_BENEFIT_FIELD.test(key)) {
      return `${formatSnapshotCurrency(value, { maximumFractionDigits: 2 })}/day`;
    }
    if (SOURCE_CURRENCY_FIELD.test(key)) {
      return formatSnapshotCurrency(value, { maximumFractionDigits: 2 });
    }
    return formatSourceNumber(value);
  }

  if (typeof value === 'string') {
    const parsedDate = /^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(value) ? new Date(value) : null;
    if (parsedDate && !Number.isNaN(parsedDate.getTime())) return parsedDate.toLocaleDateString();
    return value;
  }

  return JSON.stringify(value);
}

function sourceLeafKey(path: string): string {
  return (path.split('.').pop() ?? path).replace(/\[\d+\]/g, '');
}

function contextualSourceFieldLabel(path: string): string {
  const key = sourceLeafKey(path);
  const carePhaseSelection = path.match(/\.careEnvironmentSelections\.(\d+)$/);
  if (carePhaseSelection) {
    return `${carePhaseLabel(carePhaseSelection[1])} Care Environment`;
  }

  const careEnvironmentCost = path.match(/\.careEnvironmentCosts\.(\d+)$/);
  if (careEnvironmentCost) {
    return `${careEnvironmentLabel(careEnvironmentCost[1])} Care Cost`;
  }

  const localCareEnvironmentCost = path.match(/\.clientLocalCareEnvironmentCosts\.(\d+)\.([^.]+)$/);
  if (localCareEnvironmentCost) {
    const detailLabel =
      SOURCE_FIELD_LABELS[localCareEnvironmentCost[2]] ?? sentenceCase(localCareEnvironmentCost[2]);
    return `${careEnvironmentLabel(localCareEnvironmentCost[1])} · ${detailLabel}`;
  }

  if (key === 'length') {
    const parent =
      path
        .split('.')
        .at(-2)
        ?.replace(/\[\d+\]/g, '') ?? 'Item';
    return `${sentenceCase(parent)} Count`;
  }

  return SOURCE_FIELD_LABELS[key] ?? sentenceCase(key);
}

function sentenceCase(value: string): string {
  const words = value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return words ? `${words.charAt(0).toUpperCase()}${words.slice(1)}` : 'Field';
}

function formatSourceNumber(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(value);
}

function formatSourcePercent(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(value);
}
